import React, { useCallback, useEffect } from 'react';
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import { useThrottle } from '@features/useThrottle';
import { profileApi } from '@reducers/profile/profile';

const errorMessage = 'Profile with this name already exists.';

const AutoSubmitFilter = () => {
  const formik = useFormikContext();
  const [checkUnique] = profileApi.useLazyCheckUniqueNameQuery();
  const throttle = useThrottle((value) => {
    if (!value) {
      return;
    }
    checkUnique({ name: value }, false)
      .unwrap()
      .then((fulfilled) => {
        const { unique } = fulfilled;
        if (!unique) {
          formik.setFieldError('name', errorMessage);
          return;
        }
        formik.setErrors({});
      });
  }, 500);

  const throttledChangeHandler = useCallback(
    (value) => {
      throttle(value);
    },
    [throttle]
  );

  useEffect(() => {
    if (!formik.values.name) {
      return;
    }
    throttledChangeHandler(formik.values.name);
  }, [formik.values.name]);
};

export const AddNewProfileForm = () => {
  const navigate = useNavigate();

  const { isNameUnique } = useSelector((state) => state.profile);
  const schema = Yup.object({
    name: Yup.string()
      .test('unique-name', errorMessage, () => isNameUnique)
      .required('This is required field.'),
  });

  const [createQuery] = profileApi.useCreateProfileMutation();

  const initialValues = { name: '' };
  const onSubmit = async (values, actions) => {
    if (!isNameUnique) {
      return;
    }
    actions.setSubmitting(true);
    createQuery({ data: values })
      .unwrap()
      .then((fulfilled) => {
        const { id } = fulfilled;
        navigate(`/profiles/edit/${id}`);
      })
      .catch(() => {
        actions.setFieldError('name', 'Server-side error occurred.');
        actions.setSubmitting(false);
      });
  };

  const handleCancelButton = () => {
    navigate(-1);
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
      }) => (
        <Form onSubmit={handleSubmit} className="needs-validation">
          <Row>
            <Col>
              <FloatingLabel
                controlId="form.ControlName"
                label="Name"
                className="mb-1"
              >
                <Form.Control
                  type="text"
                  placeholder="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.name && !errors.name && isNameUnique}
                  isInvalid={touched.name && (errors.name || !isNameUnique)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
          </Row>
          <Row>
            <Col className="text-start mt-2" md={12}>
              <ButtonSplitGroup>
                <Button
                  variant="didxl"
                  onClick={handleSubmit}
                  size="sm"
                  disabled={errors.name || !isNameUnique}
                >
                  Continue
                </Button>
                <Button
                  variant="didxl-danger"
                  onClick={handleCancelButton}
                  size="sm"
                >
                  Cancel
                </Button>
              </ButtonSplitGroup>
            </Col>
          </Row>
          <AutoSubmitFilter />
        </Form>
      )}
    </Formik>
  );
};
